import { LuIcon, Text } from "@overmap-ai/blocks"
import type { User } from "@overmap-ai/common"
import { use, useCallback, useMemo } from "react"

import { UserMultiSelect } from "../../../components/DropdownMultiSelects/UserMultiSelect"
import { UserIcon } from "../../../components/Icons/UserIcon"
import { modelIconMapping } from "../../../mappings/icons"
import { getUserDisplayLabel } from "../../../utils/users"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { ProcedureStepReviewerFilterDef } from "./ProcedureStepReviewerFilterDef"

type ProcedureStepReviewerFilterCellProps = FilterDefRenderProps<ProcedureStepReviewerFilterDef>

export const ProcedureStepReviewerFilterCell = (props: ProcedureStepReviewerFilterCellProps) => {
	const { filterDef, filter, filterManager } = props

	const { size } = use(FilterRendererContext)
	const { procedureStep, users } = filterDef
	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const selectedUsers = useMemo(() => {
		if (filterValue === undefined) return undefined
		const set = new Set(filterValue)
		return users.filter(({ id }) => set.has(id))
	}, [filterValue, users])

	const handleValuesChange = useCallback(
		(users: User[]) => {
			if (users.length > 0) {
				filter.setFilterValue(users.map(({ id }) => id))
			} else {
				filter.setFilterValue(undefined)
			}
		},
		[filter],
	)

	const getFilterValueRender = () => {
		if (!filterValue || filterValue.length === 0) return "..."

		if (filterValue.length === 1) {
			return (
				<>
					<UserIcon userId={filterValue[0]!} size="xs" />
					<Text className="truncate">{getUserDisplayLabel(selectedUsers?.[0])}</Text>
				</>
			)
		}

		return (
			<span className="flex items-center gap-1 truncate">
				{filterValue.slice(0, 3).map((userId) => (
					<UserIcon key={userId} userId={userId} size="xs" />
				))}
				<Text className="truncate">{`${filterValue.length} reviewers`}</Text>
			</span>
		)
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge>
				<LuIcon icon={modelIconMapping.stage} color={procedureStep.color} fill={procedureStep.color} />
				{procedureStep.name} - Reviewers
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			<UserMultiSelect values={selectedUsers} users={users} onValuesChange={handleValuesChange} size={size}>
				<FilterCellButton className="shrink-1 min-w-0 grow truncate">{getFilterValueRender()}</FilterCellButton>
			</UserMultiSelect>

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
